import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API_URL, API_BASE_URL } from "../config/api";
import {
  MessageSquarePlus,
  Lightbulb,
  ThumbsUp,
  ThumbsDown,
  Send,
  CheckCircle,
  Clock,
  Star,
  Loader2,
  ChevronRight,
  AlertCircle,
  Sparkles,
  HeartHandshake,
  Megaphone,
} from "lucide-react";


const FEEDBACK_CATEGORIES = [
  { id: "general", label: "General Feedback", icon: MessageSquarePlus, color: "blue" },
  { id: "feature", label: "Feature Request", icon: Lightbulb, color: "amber" },
  { id: "improvement", label: "Improvement Suggestion", icon: Sparkles, color: "purple" },
  { id: "service", label: "Service Feedback", icon: HeartHandshake, color: "emerald" },
  { id: "complaint", label: "Report an Issue", icon: AlertCircle, color: "red" },
];

const RATING_OPTIONS = [
  { value: 5, label: "Excellent", emoji: "😊" },
  { value: 4, label: "Good", emoji: "🙂" },
  { value: 3, label: "Average", emoji: "😐" },
  { value: 2, label: "Poor", emoji: "😕" },
  { value: 1, label: "Very Poor", emoji: "😞" },
];

export default function CustomerFeedbackPage() {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState("submit"); // "submit" | "history"
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [feedbackHistory, setFeedbackHistory] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    category: "",
    subject: "",
    message: "",
    rating: null,
    would_recommend: null,
  });

  useEffect(() => {
    if (activeTab === "history") {
      fetchFeedbackHistory();
    }
  }, [activeTab, token]);

  const fetchFeedbackHistory = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/customer-portal/my-feedback`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeedbackHistory(response.data.feedback || []);
    } catch (error) {
      console.error("Failed to fetch feedback history");
      setFeedbackHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.category) {
      toast.error("Please select a category");
      return;
    }
    if (!formData.message.trim()) {
      toast.error("Please enter your feedback message");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/customer-portal/feedback`, {
        ...formData,
        customer_name: user?.full_name,
        customer_email: user?.email,
        customer_company: user?.company_name,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Thank you! Your feedback has been submitted.");
      setFormData({
        category: "",
        subject: "",
        message: "",
        rating: null,
        would_recommend: null,
      });
      setActiveTab("history");
      fetchFeedbackHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit feedback");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-700",
      reviewed: "bg-blue-100 text-blue-700",
      implemented: "bg-green-100 text-green-700",
      declined: "bg-gray-100 text-gray-500",
    };
    return colors[status] || "bg-gray-100 text-gray-600";
  };

  const getCategoryIcon = (categoryId) => {
    const cat = FEEDBACK_CATEGORIES.find(c => c.id === categoryId);
    return cat ? cat.icon : MessageSquarePlus;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl">
              <Megaphone className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900">Feedback & Suggestions</h1>
          </div>
          <p className="text-slate-600">Help us serve you better by sharing your thoughts and ideas</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b">
          <button
            onClick={() => setActiveTab("submit")}
            className={`flex items-center gap-2 px-4 py-3 font-medium border-b-2 transition-colors ${
              activeTab === "submit"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            data-testid="tab-submit-feedback"
          >
            <MessageSquarePlus className="w-4 h-4" />
            Share Feedback
          </button>
          <button
            onClick={() => setActiveTab("history")}
            className={`flex items-center gap-2 px-4 py-3 font-medium border-b-2 transition-colors ${
              activeTab === "history"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            data-testid="tab-feedback-history"
          >
            <Clock className="w-4 h-4" />
            My Submissions
          </button>
        </div>

        {/* Submit Feedback Form */}
        {activeTab === "submit" && (
          <div className="space-y-6">
            {/* Category Selection */}
            <div className="bg-white rounded-xl border shadow-sm p-6">
              <Label className="text-base font-semibold text-slate-800 mb-4 block">
                What would you like to share?
              </Label>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {FEEDBACK_CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => updateField("category", cat.id)}
                    className={`p-4 rounded-xl border-2 text-center transition-all ${
                      formData.category === cat.id
                        ? `border-${cat.color}-500 bg-${cat.color}-50`
                        : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                    }`}
                    data-testid={`category-${cat.id}`}
                  >
                    <cat.icon className={`w-6 h-6 mx-auto mb-2 ${
                      formData.category === cat.id ? `text-${cat.color}-600` : "text-slate-400"
                    }`} />
                    <span className="text-sm font-medium">{cat.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Feedback Details */}
            <div className="bg-white rounded-xl border shadow-sm p-6 space-y-5">
              <div>
                <Label htmlFor="subject">Subject (Optional)</Label>
                <Input
                  id="subject"
                  value={formData.subject}
                  onChange={(e) => updateField("subject", e.target.value)}
                  placeholder="Brief title for your feedback"
                  className="mt-1"
                  data-testid="feedback-subject"
                />
              </div>

              <div>
                <Label htmlFor="message">Your Feedback *</Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => updateField("message", e.target.value)}
                  placeholder="Please share your thoughts, suggestions, or describe the feature you'd like to see..."
                  rows={5}
                  className="mt-1"
                  data-testid="feedback-message"
                />
              </div>

              {/* Rating (for general/service feedback) */}
              {(formData.category === "general" || formData.category === "service") && (
                <div>
                  <Label className="mb-3 block">How would you rate our service?</Label>
                  <div className="flex gap-2">
                    {RATING_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => updateField("rating", opt.value)}
                        className={`flex-1 p-3 rounded-lg border-2 text-center transition-all ${
                          formData.rating === opt.value
                            ? "border-blue-500 bg-blue-50"
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                        data-testid={`rating-${opt.value}`}
                      >
                        <span className="text-2xl block">{opt.emoji}</span>
                        <span className="text-xs text-slate-600 mt-1 block">{opt.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Would Recommend */}
              {formData.category && formData.category !== "complaint" && (
                <div>
                  <Label className="mb-3 block">Would you recommend Europacific to others?</Label>
                  <div className="flex gap-3">
                    <button
                      onClick={() => updateField("would_recommend", true)}
                      className={`flex-1 p-4 rounded-xl border-2 flex items-center justify-center gap-2 transition-all ${
                        formData.would_recommend === true
                          ? "border-green-500 bg-green-50 text-green-700"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                      data-testid="recommend-yes"
                    >
                      <ThumbsUp className="w-5 h-5" />
                      <span className="font-medium">Yes, definitely!</span>
                    </button>
                    <button
                      onClick={() => updateField("would_recommend", false)}
                      className={`flex-1 p-4 rounded-xl border-2 flex items-center justify-center gap-2 transition-all ${
                        formData.would_recommend === false
                          ? "border-red-500 bg-red-50 text-red-700"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                      data-testid="recommend-no"
                    >
                      <ThumbsDown className="w-5 h-5" />
                      <span className="font-medium">Not yet</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex justify-end">
              <Button
                onClick={handleSubmit}
                disabled={submitting || !formData.category || !formData.message.trim()}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 px-8"
                data-testid="submit-feedback-btn"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Submit Feedback
              </Button>
            </div>
          </div>
        )}

        {/* Feedback History */}
        {activeTab === "history" && (
          <div>
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : feedbackHistory.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-xl border">
                <MessageSquarePlus className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-600">No feedback submitted yet</h3>
                <p className="text-slate-400 mt-1">Share your first feedback to help us improve</p>
                <Button className="mt-4" onClick={() => setActiveTab("submit")}>
                  <Lightbulb className="w-4 h-4 mr-2" />
                  Share Feedback
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {feedbackHistory.map((item) => {
                  const CategoryIcon = getCategoryIcon(item.category);
                  return (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-white rounded-xl border shadow-sm p-5"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-slate-100 rounded-lg">
                            <CategoryIcon className="w-5 h-5 text-slate-600" />
                          </div>
                          <div>
                            <span className="font-medium text-slate-800">
                              {item.subject || FEEDBACK_CATEGORIES.find(c => c.id === item.category)?.label}
                            </span>
                            <p className="text-xs text-slate-400">
                              {new Date(item.created_at).toLocaleDateString("en-GB", {
                                day: "numeric",
                                month: "short",
                                year: "numeric"
                              })}
                            </p>
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                          {item.status || "pending"}
                        </span>
                      </div>
                      <p className="text-slate-600 text-sm line-clamp-2">{item.message}</p>
                      
                      {item.response && (
                        <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                          <p className="text-xs font-medium text-blue-700 mb-1">Response from Europacific:</p>
                          <p className="text-sm text-blue-800">{item.response}</p>
                        </div>
                      )}

                      {item.rating && (
                        <div className="mt-3 flex items-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`w-4 h-4 ${i < item.rating ? "text-amber-400 fill-amber-400" : "text-slate-200"}`}
                            />
                          ))}
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Encouragement Message */}
        <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-blue-100">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-white rounded-xl shadow-sm">
              <HeartHandshake className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800">Your voice matters!</h3>
              <p className="text-sm text-slate-600 mt-1">
                Every piece of feedback helps us improve our services. Feature requests are reviewed monthly 
                and prioritized based on customer demand.
              </p>
              <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-800 font-medium flex items-center gap-2">
                  <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                  <span>Reward for great ideas!</span>
                </p>
                <p className="text-sm text-amber-700 mt-1">
                  If your suggestion is implemented, we'll issue you a <strong>€100 voucher</strong> to use towards your future shipments!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
